import { Button, Chip, Group, Paper, Text, TextInput } from '@mantine/core'
import { IconPrinter, IconWorldWww } from '@tabler/icons-react'
import { formatPhone } from '@/utils'
import {
  distinctPracticeName,
  FAX_SOURCE_LABEL,
  isValidFax,
  recipientName,
  type SelectedRecipient,
} from './referralTypes'

type Props = {
  recipient: SelectedRecipient
  onFaxChange: (fax: string) => void
  onClear: () => void
}

const digits = (v: string) => v.replace(/\D/g, '').slice(-10)

// The fax is the one field that must be right: a misdirected fax is a presumed HIPAA
// breach. Saved, directory and NPPES numbers are matched to this practice's address; the
// practice website number is practice-level and not address-matched. Either way the
// clinician confirms one or types the right number.
export const RecipientCard = ({ recipient, onFaxChange, onClear }: Props) => {
  const fax = recipient.fax.trim()
  const error = !fax
    ? 'No fax number found — enter one to continue'
    : !isValidFax(fax)
      ? 'Enter a 10-digit US fax number'
      : undefined
  const fromWebsite = recipient.faxCandidates.some(
    (c) => c.source === 'website' && digits(c.fax) === digits(fax),
  )
  return (
    <Paper withBorder p="md" className="border-blue-500 bg-blue-50/40">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div className="min-w-0">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            Referring to
          </Text>
          <Text fw={700}>{recipientName(recipient)}</Text>
          {distinctPracticeName(recipient) && (
            <Text size="sm">{distinctPracticeName(recipient)}</Text>
          )}
          <Text size="sm" c="dimmed">
            {recipient.specialtyLabel}
          </Text>
          <Text size="xs" c="dimmed">
            {recipient.street}, {recipient.city}, {recipient.state} {recipient.zip}
          </Text>
          <Text size="xs" c="dimmed">
            {[
              recipient.phone && `Phone ${recipient.phone}`,
              recipient.npi && `NPI ${recipient.npi}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Text>
        </div>
        <Button variant="subtle" size="xs" onClick={onClear}>
          Change
        </Button>
      </Group>
      <TextInput
        mt="sm"
        label="Fax number"
        description="The referral is faxed to this number. Confirm it, or enter the right one."
        withAsterisk
        leftSection={<IconPrinter size={16} />}
        value={recipient.fax}
        onChange={(e) => onFaxChange(e.currentTarget.value)}
        error={error}
      />
      {fromWebsite && !error && (
        <Group gap={6} mt={6} wrap="nowrap" c="orange.8">
          <IconWorldWww size={14} style={{ flexShrink: 0 }} />
          <Text size="xs" c="orange.8">
            Found on the practice's website, not matched to this office's address. Confirm it's the
            right number before sending.
          </Text>
        </Group>
      )}
      {recipient.faxCandidates.length > 0 && (
        <Group gap={6} mt={6}>
          <Text size="xs" c="dimmed">
            Numbers on file:
          </Text>
          {recipient.faxCandidates.map((c) => (
            <Chip
              key={c.fax}
              size="xs"
              checked={digits(c.fax) === digits(recipient.fax)}
              onChange={() => onFaxChange(c.fax)}
            >
              {formatPhone(c.fax)} · {FAX_SOURCE_LABEL[c.source]}
            </Chip>
          ))}
        </Group>
      )}
    </Paper>
  )
}
